import { Stack, Text, Title } from '@mantine/core';
import { type ReactNode } from 'react';
import { useTranslation } from 'react-i18next';

import classes from './empty-state.module.css';

export interface EmptyStateProps {
  readonly titleKey: string;
  /** What to do next. Absent only when the emptiness is genuinely terminal. */
  readonly descriptionKey?: string;
  /** The primary action itself — a `Button`, a `Link`; the caller owns what it does. */
  readonly action?: ReactNode;
  /**
   * Which heading level this state occupies, and there are exactly two answers.
   *
   * Directly under the page title it is `h2` — the default, and what most screens want. Inside a
   * `Section`, whose own heading is already `h2`, that produces two headings of the same level with
   * nothing between them: to anybody navigating by headings the empty list reads as a *sibling
   * section* of the one it is inside rather than as its content (`rules/a11y.mdc` §20).
   *
   * A closed pair rather than a number, because there is no third place: `h4` under an `h2` is a
   * hole in the outline, and `h1` is owned by `PageHeader`.
   */
  readonly headingOrder?: 2 | 3;
}

/**
 * «Nothing here» with a next step (`ux-architecture.md` → принцип 3).
 *
 * An empty state that only says «no data» is a dead end; the product rule is that it explains what
 * would put something here, which is why `titleKey` alone is not the whole component. Having it in
 * `shared/ui` is what stops fifteen screens from inventing fifteen different dead ends.
 */
export function EmptyState({
  titleKey,
  descriptionKey,
  action,
  headingOrder = 2,
}: EmptyStateProps) {
  const { t } = useTranslation();

  return (
    <Stack align="center" className={classes['root']} gap="sm" data-testid="empty-state">
      {/* `size` stays `h3` at either level: the level is structure, the size is typography, and
          tying them together is what makes authors reach for the wrong tag to get the right look
          (`rules/design-system.mdc` §14). */}
      <Title order={headingOrder} size="h3">
        {t(titleKey)}
      </Title>
      {descriptionKey !== undefined && (
        <Text c="var(--lw-text-muted)" ta="center">
          {t(descriptionKey)}
        </Text>
      )}
      {action}
    </Stack>
  );
}
